import {useFormik} from 'formik';
import {useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import * as Yup from 'yup';
import {AlertProps} from '../../../../common/components/alerts/Types';
import {HttpError} from '../../../../common/hooks/ApiClient';
import {useMutation} from '../../../../common/hooks/QueryHooks';
import {routes} from '../../../../routing/routes';
import {Enterprise} from '../../common/Types';
import {OTPValidated} from '../../common/components/otp/Types';
import {useI18n} from '../../../../common/providers/I18nProvider';

const useReset = () => {
	const [loading, setLoading] = useState(false);
	const [showPassword, setShowPassword] = useState(false);

	const {mutate} = useMutation<Enterprise>('/enterpriser/auth/reset-password');

	const navigate = useNavigate();
	const location = useLocation();

	const {email, code} = (location.state as OTPValidated) ?? {};

	const {i18n} = useI18n();
	const schema = Yup.object().shape({
		newPassword: Yup.string()
			.min(3, i18n('AUTH.RESET.VALIDATION.NEW.MIN', {val: 3}))
			.max(50, i18n('AUTH.RESET.VALIDATION.NEW.MAX', {val: 50}))
			.required('New Password is required'),
		confirmPassword: Yup.string()
			.oneOf([Yup.ref('newPassword')], i18n('AUTH.RESET.VALIDATION.CONFIRM.MATCH'))
			.required(i18n('AUTH.RESET.VALIDATION.CONFIRM.REQUIRED')),
	});

	const toggleShowPassword = () => {
		setShowPassword((current) => !current);
	};

	const formik = useFormik({
		initialValues: {
			newPassword: undefined,
			confirmPassword: undefined,
		},
		validationSchema: schema,
		onSubmit: async (values, {setStatus, setSubmitting}) => {
			setLoading(true);
			try {
				const data = {email, password: values.newPassword, confirmPassword: values.confirmPassword, code};
				await mutate({data});
				navigate(routes.login, {
					state: {
						title: i18n('AUTH.RESET.SUCCESS.ALERT.TITLE'),
						message: i18n('AUTH.RESET.SUCCESS.ALERT.MESSAGE'),
						showTitle: true,
					} as AlertProps,
				});
			} catch (error) {
				if (error instanceof HttpError) {
					setStatus(error.message);
				}
			} finally {
				setSubmitting(false);
				setLoading(false);
			}
		},
	});

	const canNotSubmit = formik.isSubmitting || !formik.isValid || !formik.touched.newPassword;

	return {loading, formik, showPassword, toggleShowPassword, canNotSubmit};
};

export default useReset;
